import { action, makeObservable, observable } from 'mobx'

import {
  ICalendarConfiguration,
  ITenantLink,
  SharingMode,
} from '~/client/graph'
import {
  GetCalendarConfigurationDocument,
  UpdateCalendarDeliverySharingModeDocument,
  UpdateCalendarFormSharingModeDocument,
  UpdateProjectCalendarSharingDocument,
  UpdateProjectDeliveryDateRangeDocument,
  UpdateProjectFormDateRangeDocument,
} from '~/client/graph/operations/generated/CalendarConfiguration.generated'
import { GenerateSubscriberForExternalCalendarsDocument } from '~/client/graph/operations/generated/CalendarSubscriber.generated'
import {
  GetTenantLinksDocument,
  SubscribeToTenantLinkDocument,
  UnsubscribeFromTenantLinkDocument,
} from '~/client/graph/operations/generated/TenantLink.generated'
import { ExternalCalendarDateRange } from '~/client/src/desktop/enums/ExternalCalendarDateRange'
import { ExternalCalendarDateRangeCommon } from '~/client/src/desktop/enums/ExternalCalendarDateRangeCommon'
import { ExternalCalendarWorkflowType } from '~/client/src/desktop/enums/ExternalCalendarWorkflowType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { toast } from '~/client/src/shared/utils/toast'

export enum DateEndOption {
  EndIn30Days = 'End in 30 days',
  EndIn90Days = 'End in 90 days',
  EndIn365Days = 'End in 365 days',
  Custom = 'Custom',
}

const DATE_OPTIONS = {
  [DateEndOption.EndIn30Days]: 30,
  [DateEndOption.EndIn90Days]: 90,
  [DateEndOption.EndIn365Days]: 365,
}

const MESSAGES = {
  iCalSharingLinkCopied: 'iCal sharing link has been copied to clipboard',
  failedToGenerateSubscriber:
    'Failed to generate subscriber for external calendars.',
  projectCodeNotDefined: 'Project code is not defined.',
  failedToGenerateICalLink: 'Failed to generate iCal link.',
  invalidUrlFormat: `Invalid URL format. Expected: 
  https://{subdomain}.{struxsite.com|struxhub.com}/calendar/stx/name/{projectCode}/export`,
}

const importUrlPattern =
  /^https?:\/\/\S+\.(struxsite|struxhub)\.com\/calendar\/stx\/name\/[^\s/]+\/export$/

export default class CalendarLinkInputStore {
  @observable public isCustomFormDateEndOption: boolean = false
  @observable public isCustomDeliveryDateEndOption: boolean = false
  @observable public isDisableAccessDialogShown: boolean = false
  @observable public isRemoveSubscriptionDialogShown: boolean = false
  @observable public subscriptionIdToRemove: string = ''
  @observable public inputValue: string = ''
  @observable public isEnabled: boolean = false
  @observable public calendarDeliverySharingMode: SharingMode =
    SharingMode.FromRules
  @observable public calendarFormSharingMode: SharingMode =
    SharingMode.FromRules
  @observable public deliverySharingFrom: string = null
  @observable public deliverySharingTo: string = null
  @observable public formSharingFrom: string = null
  @observable public formSharingTo: string = null
  @observable public openTo: ExternalCalendarDateRange =
    ExternalCalendarDateRange.NONE
  @observable public viewingDate: Date = null
  @observable public isSwitchDisabled: boolean = false
  @observable public subscriptions: ITenantLink[] = []
  @observable public importInputValue: string = ''
  @observable public importInputError: string = ''
  @observable public isLoading: boolean = false
  @observable public stxLinkInputValue: string = ''
  @observable public externalLinkGeneratorLoading = false

  public constructor(
    private readonly graphExecutorStore: GraphExecutorStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly projectId: string,
    private readonly projectCode: string,
  ) {
    makeObservable(this)
  }

  @action.bound
  public setIsRemoveSubscriptionDialogShown(
    value: boolean,
    subscriptionId: string,
  ) {
    this.isRemoveSubscriptionDialogShown = value
    this.subscriptionIdToRemove = subscriptionId
  }

  @action.bound
  public setIsDisableAccessDialogShown(
    value: boolean,
    shouldDisable: boolean = false,
  ) {
    this.isDisableAccessDialogShown = value
    if (shouldDisable) {
      this.changeCalendarSharing()
    }
  }

  @action.bound
  public setImportInputValue(event: React.ChangeEvent<HTMLInputElement>) {
    this.importInputValue = event.target.value.trim()
    this.validateImportUrl(this.importInputValue)
  }

  @action.bound
  private clearImportInput() {
    this.importInputValue = ''
  }

  @action.bound
  private setImportInputError(error: string) {
    this.importInputError = error
  }

  private validateImportUrl(url: string) {
    if (!url || importUrlPattern.test(url)) {
      return this.setImportInputError('')
    }

    this.setImportInputError(MESSAGES.invalidUrlFormat)
  }

  @action.bound
  private updateInputValue(newValue: string) {
    this.inputValue = newValue
  }

  @action.bound
  private setIsLoading(value: boolean) {
    this.isLoading = value
  }

  @action.bound
  private toggleExternalLinkGeneratorIsLoading() {
    this.externalLinkGeneratorLoading = !this.externalLinkGeneratorLoading
  }

  @action.bound
  private setSubscriptions(subscriptions: ITenantLink[]) {
    this.subscriptions = subscriptions ?? []
  }

  @action.bound
  private setSwitchDisabled(isDisabled: boolean) {
    this.isSwitchDisabled = isDisabled
  }

  @action.bound
  private setIsEnabled(value: boolean) {
    this.isEnabled = value
  }

  @action.bound
  private setCalendarFormSharingMode(value: SharingMode) {
    this.calendarFormSharingMode = value
  }

  @action.bound
  private setCalendarDeliverySharingMode(value: SharingMode) {
    this.calendarDeliverySharingMode = value
  }

  @action.bound
  private setDeliverySharingFrom(newDate: string) {
    this.deliverySharingFrom = newDate
  }

  @action.bound
  public setDeliverySharingTo(newDate: string) {
    this.deliverySharingTo = newDate
  }

  @action.bound
  private setFormSharingFrom(newDate: string) {
    this.formSharingFrom = newDate
  }

  @action.bound
  public setFormSharingTo(newDate: string) {
    this.formSharingTo = newDate
  }

  @action.bound
  private initStoreObservables(
    calendarConfiguration: ICalendarConfiguration,
    calendarSubscriptions: ITenantLink[] = null,
  ) {
    const {
      calendarFormSharingMode,
      calendarDeliverySharingMode,
      formSharingFrom,
      formSharingTo,
      deliverySharingFrom,
      deliverySharingTo,
    } = calendarConfiguration

    this.setFormSharingFrom(formSharingFrom)
    this.setFormSharingTo(formSharingTo)
    this.setSubscriptions(calendarSubscriptions)
    this.setCalendarFormSharingMode(calendarFormSharingMode)
    this.setDeliverySharingFrom(deliverySharingFrom)
    this.setDeliverySharingTo(deliverySharingTo)
    this.setCalendarDeliverySharingMode(calendarDeliverySharingMode)
  }

  public toggleDisableAccessDialog = (): void => {
    if (!this.isEnabled) {
      this.changeCalendarSharing()
    } else {
      this.setIsDisableAccessDialogShown(true)
    }
  }

  public changeCalendarSharing = async () => {
    try {
      this.setSwitchDisabled(true)
      this.setIsEnabled(!this.isEnabled)

      await this.graphExecutorStore.mutate(
        UpdateProjectCalendarSharingDocument,
        {
          projectId: this.projectId,
          calendarSharing: this.isEnabled,
        },
      )

      const calendarConfig = await this.graphExecutorStore.query(
        GetCalendarConfigurationDocument,
        { projectId: this.projectId },
      )

      this.initStoreObservables(calendarConfig.data.getCalendarConfiguration)

      if (this.isEnabled && calendarConfig) {
        await this.generateLink()
      }
    } catch (error) {
      toast.error(Localization.translator.somethingWentWrongTryAgainLater)
      console.error(error)
    } finally {
      this.setSwitchDisabled(false)
    }
  }

  public initialize = async () => {
    try {
      this.toggleExternalLinkGeneratorIsLoading()
      const [calendarSubscriptions, calendarConfigurationQuery] =
        await Promise.all([
          this.graphExecutorStore.query(GetTenantLinksDocument, {
            projectId: this.projectId,
          }),
          this.graphExecutorStore.query(GetCalendarConfigurationDocument, {
            projectId: this.projectId,
          }),
        ])

      const calendarConfiguration =
        calendarConfigurationQuery.data.getCalendarConfiguration

      if (calendarConfiguration) {
        this.initStoreObservables(
          calendarConfiguration,
          calendarSubscriptions.data.tenantLinks.data,
        )
        this.setIsEnabled(calendarConfiguration.calendarSharing)
        await this.generateLink()
      }
    } catch (error) {
      toast.error(Localization.translator.somethingWentWrongTryAgainLater)
      console.error(error)
    } finally {
      this.toggleExternalLinkGeneratorIsLoading()
    }
  }

  private calculateEndDate(startDate: string, days: number): string {
    return this.projectDateStore
      .addDays(new Date(startDate), days)
      .toISOString()
  }

  private async updateDeliveryDateRange(
    from: string,
    to: string,
  ): Promise<void> {
    await this.graphExecutorStore.mutate(
      UpdateProjectDeliveryDateRangeDocument,
      {
        projectId: this.projectId,
        deliveryFrom: from ? from : null,
        deliveryTo: to ? to : null,
      },
    )
  }

  private async updateFormDateRange(from: string, to: string): Promise<void> {
    await this.graphExecutorStore.mutate(UpdateProjectFormDateRangeDocument, {
      projectId: this.projectId,
      formFrom: from ? from : null,
      formTo: to ? to : null,
    })
  }

  public changeDeliverySharingDateRange = (
    key: ExternalCalendarDateRangeCommon,
    date: string,
  ) => {
    if (key === ExternalCalendarDateRangeCommon.FROM) {
      this.setDeliverySharingFrom(date)
    } else {
      this.setDeliverySharingTo(date)
    }

    this.updateDeliveryDateRange(
      this.deliverySharingFrom,
      this.deliverySharingTo,
    )
  }

  public changeFormSharingDateRange = (
    key: ExternalCalendarDateRangeCommon,
    date: string,
  ) => {
    if (key === ExternalCalendarDateRangeCommon.FROM) {
      this.setFormSharingFrom(date)
    } else {
      this.setFormSharingTo(date)
    }

    this.updateFormDateRange(this.formSharingFrom, this.formSharingTo)
  }

  public setSharingDateRange = (
    setDateFunction: (date: string) => void,
    workflowType: ExternalCalendarWorkflowType,
    dateEndOption: DateEndOption,
  ) => {
    const isForm = workflowType === ExternalCalendarWorkflowType.FORM
    const startDate = isForm ? this.formSharingFrom : this.deliverySharingFrom

    const isCustomDate = dateEndOption === DateEndOption.Custom
    if (isForm) {
      this.isCustomFormDateEndOption = isCustomDate
    } else {
      this.isCustomDeliveryDateEndOption = isCustomDate
    }
    if (isCustomDate) return
    const days = DATE_OPTIONS[dateEndOption]
    setDateFunction(this.calculateEndDate(startDate, days))

    if (isForm) {
      this.updateFormDateRange(this.formSharingFrom, this.formSharingTo)
    } else {
      this.updateDeliveryDateRange(
        this.deliverySharingFrom,
        this.deliverySharingTo,
      )
    }
  }

  private generateLink = async (): Promise<void> => {
    try {
      const response = await this.graphExecutorStore.mutate(
        GenerateSubscriberForExternalCalendarsDocument,
        {},
      )

      if (!response?.data?.generateSubscriberForExternalCalendars) {
        throw new Error(MESSAGES.failedToGenerateSubscriber)
      }

      if (!this.projectCode) {
        throw new Error(MESSAGES.projectCodeNotDefined)
      }

      const { privateCode } =
        response.data.generateSubscriberForExternalCalendars
      const link = `${window.location.origin}/calendar/name/${this.projectCode}/export/${privateCode}`

      this.updateInputValue(link)
      this.generateStxToICalLink()
    } catch (error) {
      console.error(error)
      throw new Error(MESSAGES.failedToGenerateICalLink)
    }
  }

  public copyToClipboard = () => {
    if (!this.inputValue) return
    navigator.clipboard.writeText(this.inputValue).then(() => {
      toast.success(MESSAGES.iCalSharingLinkCopied)
    })
  }

  public copyLinkToClipboard = (link: string) => {
    if (!link) return
    navigator.clipboard.writeText(link).then(() => {
      toast.success(Localization.translator.copiedToClipboard)
    })
  }

  public changeCalendarFormSharingMode = (sharingMode: SharingMode) => {
    this.setCalendarFormSharingMode(sharingMode)
    this.graphExecutorStore.mutate(UpdateCalendarFormSharingModeDocument, {
      projectId: this.projectId,
      sharingMode,
    })
  }

  public changeCalendarDeliverySharingMode = (sharingMode: SharingMode) => {
    this.setCalendarDeliverySharingMode(sharingMode)
    this.graphExecutorStore.mutate(UpdateCalendarDeliverySharingModeDocument, {
      projectId: this.projectId,
      sharingMode,
    })
  }

  @action.bound
  private generateStxToICalLink(
    code: string = null,
    privateCode: string = null,
  ): void {
    const baseLink = `${window.location.origin}/calendar/stx/name/${this.projectCode}/export`
    this.stxLinkInputValue = code ? `${baseLink}/${privateCode}` : baseLink
  }

  public copyToClipboardSTX = async (
    code: string = null,
    privateCode: string = null,
  ): Promise<void> => {
    this.generateStxToICalLink(code, privateCode)
    await navigator.clipboard.writeText(this.stxLinkInputValue)
    toast.success(
      Localization.translator.linkForProjectCopiedToClipboard(this.projectCode),
    )
  }

  private extractSegment(url: string): string {
    if (!url) return null
    const regex = /\/name\/([^/]+)/
    const match = url.match(regex)
    return match?.[1]
  }

  @action.bound
  public async unsubscribe(id: string): Promise<void> {
    try {
      await this.graphExecutorStore.mutate(UnsubscribeFromTenantLinkDocument, {
        tenantLinkId: id,
      })

      toast.success(Localization.translator.unsubscribedSuccessfully)
      this.setSubscriptions(
        this.subscriptions.filter(subscription => subscription.id !== id),
      )
    } catch (error) {
      toast.error(Localization.translator.somethingWentWrongTryAgainLater)
      console.error(error)
    } finally {
      this.setIsRemoveSubscriptionDialogShown(false, null)
    }
  }

  @action.bound
  public async subscribe(link: string): Promise<string> {
    if (!link) {
      toast.error(Localization.translator.provideLink)
      return
    }

    if (!this.isImportUrlValid) {
      toast.error(MESSAGES.invalidUrlFormat)
      return
    }

    if (this.extractSegment(link) === this.projectCode) {
      toast.error(Localization.translator.youCannotSubscribeToYourOwnProject)
      return
    }

    try {
      this.setIsLoading(true)
      toast.info(Localization.translator.fetchingEventsOnBackground)

      const tenantLink = await this.graphExecutorStore.mutate(
        SubscribeToTenantLinkDocument,
        {
          tenantLink: link,
          projectId: this.projectId,
        },
      )

      this.setSubscriptions([
        ...this.subscriptions,
        tenantLink.data.subscribeToTenantLink,
      ])
      this.clearImportInput()
      toast.success(Localization.translator.importedSuccessfully)
      return tenantLink.data.subscribeToTenantLink.tenantCalendarEndpoint
    } catch (error) {
      toast.error(Localization.translator.somethingWentWrongTryAgainLater)
      console.error(error)
    } finally {
      this.setIsLoading(false)
    }
  }

  public get currentFormSharingEndOption(): DateEndOption {
    return this.isCustomFormDateEndOption
      ? DateEndOption.Custom
      : this.currentSharingEndOption(ExternalCalendarWorkflowType.FORM)
  }

  public get currentDeliverySharingEndOption(): DateEndOption {
    return this.isCustomDeliveryDateEndOption
      ? DateEndOption.Custom
      : this.currentSharingEndOption(ExternalCalendarWorkflowType.DELIVERY)
  }

  private currentSharingEndOption(
    workflowType: ExternalCalendarWorkflowType,
  ): DateEndOption {
    const from =
      workflowType === ExternalCalendarWorkflowType.FORM
        ? this.formSharingFrom
        : this.deliverySharingFrom
    const to =
      workflowType === ExternalCalendarWorkflowType.FORM
        ? this.formSharingTo
        : this.deliverySharingTo

    const days =
      this.projectDateStore.getDaysInInterval(new Date(from), new Date(to))
        .length - 1

    for (const [option, value] of Object.entries(DATE_OPTIONS)) {
      if (days === value) return option as DateEndOption
    }

    return DateEndOption.Custom
  }

  @action.bound
  public setIsOpen(value: ExternalCalendarDateRange) {
    this.openTo = value
  }

  @action.bound
  public setViewingDate(date: Date) {
    this.viewingDate = date
  }

  public get isImportUrlValid(): boolean {
    return !!this.importInputValue && !this.importInputError
  }
}
